import { Navigate, Outlet, useLocation } from 'react-router';
import { useAuth, type AuthState } from '../lib/auth';
import { BrandLogo } from './BrandLogo';

function Loading() {
  return (
    <main
      className="flex min-h-dvh flex-col items-center justify-center gap-3 text-sm text-muted"
      aria-busy
    >
      <span className="motion-safe:animate-pulse">
        <BrandLogo size={56} wordmark={false} />
      </span>
      Loading…
    </main>
  );
}

/** Where a signed-in user belongs: the confirm-email gate (D25), onboarding, or the app. */
export function homeFor(auth: Extract<AuthState, { status: 'authed' }>): string {
  if (!auth.user.emailVerifiedAt) return '/confirm-email';
  return auth.household ? '/' : '/onboarding';
}

/**
 * Signed-in users with a confirmed email only. Anonymous visitors go to
 * /login?next=…; unverified accounts go to /confirm-email (the API would refuse
 * them with 403 EMAIL_NOT_VERIFIED anyway).
 */
export function RequireAuth() {
  const { auth } = useAuth();
  const location = useLocation();
  if (auth.status === 'loading') return <Loading />;
  if (auth.status === 'anon') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!auth.user.emailVerifiedAt) return <Navigate to="/confirm-email" replace />;
  return <Outlet />;
}

/** The confirm-email screen: signed in, not yet verified. Anyone else is sent where they belong. */
export function RequireUnverified() {
  const { auth } = useAuth();
  if (auth.status === 'loading') return <Loading />;
  if (auth.status === 'anon') return <Navigate to="/login" replace />;
  if (auth.user.emailVerifiedAt) return <Navigate to={homeFor(auth)} replace />;
  return <Outlet />;
}

/** Signed-in, verified users with an active household; others go to onboarding. */
export function RequireHousehold() {
  const { auth } = useAuth();
  if (auth.status === 'loading') return <Loading />;
  if (auth.status === 'anon') return <Navigate to="/login" replace />;
  if (!auth.user.emailVerifiedAt) return <Navigate to="/confirm-email" replace />;
  if (!auth.household) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

export function RequireAdmin() {
  const { auth } = useAuth();
  if (auth.status === 'loading') return <Loading />;
  if (auth.status === 'anon') return <Navigate to="/login?next=%2Fadmin" replace />;
  if (!auth.user.emailVerifiedAt) return <Navigate to="/confirm-email" replace />;
  if (!auth.user.isAdmin) return <Navigate to="/" replace />;
  return <Outlet />;
}

/** Login/register pages: already-signed-in users are sent home. */
export function AnonOnly() {
  const { auth } = useAuth();
  if (auth.status === 'loading') return <Loading />;
  if (auth.status === 'authed') return <Navigate to={homeFor(auth)} replace />;
  return <Outlet />;
}
